import { useMemo } from 'react';

import { type LibraryWithPermissions } from '@air/api/types';
import {
  SettingsIcon as GearIcon,
  LogOutIcon,
  UserRoundPlusIcon as MemberAddIcon,
  ArrowUpDownIcon as SortIcon,
  Trash2Icon as TrashIcon,
} from '@air/design-icons';
import { type DropdownMenuOption } from '@air/primitive-dropdown-menu';
import { useAirModal } from '@air/provider-modal';

import { useLibrary } from '~/components/LibraryBeta/hooks/queries/useLibrary';
import { useLibraryPermissions } from '~/components/LibraryBeta/hooks/useLibraryPermissions';
import { useSortMenuOptions } from '~/components/LibraryBeta/hooks/useSortMenuOptions';
import { LibraryDeleteConfirmationModal } from '~/components/LibraryBeta/LibraryDeleteConfirmationModal/LibraryDeleteConfirmationModal';
import { LibraryLeaveModal } from '~/components/LibraryBeta/LibraryLeaveModal/LibraryLeaveModal';
import { LibrarySettingsModal } from '~/components/LibraryBeta/LibrarySettingsModal/LibrarySettingsModal';
import {
  canAddOthersToPrivateOrHiddenLibrary,
  canAddOthersToPublicLibrary,
  canDeleteLibrary,
} from '~/utils/permissions/libraryPermissions';

export type UseLibraryMenuOptionsParams = {
  initialData?: LibraryWithPermissions;
  libraryId: LibraryWithPermissions['id'];
};

export const useLibraryMenuOptions = ({ initialData, libraryId }: UseLibraryMenuOptionsParams) => {
  const { libraryPermissions } = useLibraryPermissions({ libraryId });

  const [setShowLibrarySettingsModal] = useAirModal(LibrarySettingsModal);
  const [setShowLibraryDeleteConfirmationModal] = useAirModal(LibraryDeleteConfirmationModal);
  const [setShowLibraryLeaveModal] = useAirModal(LibraryLeaveModal);
  const { data: library } = useLibrary({ initialData, libraryId });
  const { sortDropdownMenuOptions } = useSortMenuOptions({ libraryId, location: 'library' });

  const canDelete = canDeleteLibrary(libraryPermissions);
  const canInvite =
    library?.accessibility === 'public'
      ? canAddOthersToPublicLibrary(libraryPermissions)
      : canAddOthersToPrivateOrHiddenLibrary(libraryPermissions);

  const menuOptions = useMemo(() => {
    if (!library) {
      return [];
    }

    const options: DropdownMenuOption[] = [
      {
        id: 'library-settings',
        label: 'Library settings',
        onSelect: () => {
          setShowLibrarySettingsModal({
            defaultTab: 'details',
            libraryId,
          });
        },
        prefix: <GearIcon size="sm" />,
        type: 'item',
      },
    ];

    if (sortDropdownMenuOptions.length > 0) {
      options.push({
        id: 'sort-boards',
        label: 'Sort boards',
        prefix: <SortIcon size="sm" />,
        type: 'sub',
        options: sortDropdownMenuOptions,
      });
    }
    if (canInvite) {
      options.push({
        id: 'invite-users',
        label: 'Invite users',
        onSelect: () => {
          setShowLibrarySettingsModal({
            defaultTab: 'members',
            libraryId,
          });
        },
        prefix: <MemberAddIcon size="sm" />,
        type: 'item',
      });
    }

    /**
     * RoleId exists only if they are a member of the library
     */
    if (library.roleId) {
      options.push({
        id: 'leave-library',
        label: 'Leave library',
        onSelect: () => {
          setShowLibraryLeaveModal({ id: library.id, title: library.title });
        },
        prefix: <LogOutIcon size="sm" />,
        type: 'item',
      });
    }

    if (canDelete) {
      options.push({
        type: 'separator',
        id: 'separator',
      });
      options.push({
        className: '!text-red-11',
        id: 'delete-library',
        label: 'Delete library',
        onSelect: () => {
          setShowLibraryDeleteConfirmationModal({
            libraryId,
            name: library.title,
          });
        },
        prefix: <TrashIcon size="sm" />,
        type: 'item',
      });
    }

    return options;
  }, [
    library,
    canDelete,
    sortDropdownMenuOptions,
    canInvite,
    setShowLibrarySettingsModal,
    libraryId,
    setShowLibraryLeaveModal,
    setShowLibraryDeleteConfirmationModal,
  ]);

  return { menuOptions };
};
